import React, { useState, useEffect, useMemo, useRef } from "react";
import { useRulesStore } from "@/store/rulesStore";
import type { ValidationResult } from "@/types/rules";
import { IssueManagementPanel } from "@/components/rules/IssueManagementPanel";
import { loadComplianceReportsForModel } from "@/services/dataService";
import { useViewerStore } from "@/store/viewerStore";
import { toast } from "@/components/common/toastUtils";

interface ValidationReport {
  // Common fields for both toilet and door reports
  report_type?: 'toilet' | 'door';
  wc_db_id?: number;  // For toilet reports
  door_db_id?: number;  // For door reports
  room_name?: string;
  room_number?: string;
  validation_metadata?: {
    room_id?: string | number;
    room_name?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
    validation_date?: string;
  };
  metadata?: {
    element_id?: number;
    element_name?: string;
    element_type?: string;
    validation_date?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
  };
  toilet_element_id?: string | number;
  standard?: string;
  // v5 format (rule_validations) - used by door and toilet compliance reports
  rule_validations?: Array<{
    rule_number: string;
    rule_name: string;
    rule_raw_text?: string;
    status: string;
    warning_type?: string | null;
    warning_message?: string | null;
    involved_db_ids?: number[];
    involved_elements?: any[];
    measured_value?: number | string | null;
    required_value?: string | null;
    unit?: string | null;
    llm_reasoning?: string | null;
    llm_notes?: string[];
    confidence?: string;
    visible?: boolean;
  }>;
  regulation_validation_results?: Array<{
    regulation_id: string;
    title: string;
    requirement?: string;
    status: string;
    measured_value?: string | number;
    required_value?: string;
    visible?: boolean;
    section?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string;
    entities_used?: Array<{
      dbId?: number;
      elementId?: number;
      name?: string;
      type?: string;
    }>;
  }>;
  validation_results?: Array<{
    regulation_id: string;
    title: string;
    requirement?: string;
    status: string;
    measured_value?: string | number;
    required_value?: string;
    visible?: boolean;
    section?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string;
    entities_used?: Array<{
      dbId?: number;
      elementId?: number;
      name?: string;
      type?: string;
    }>;
  }>;
  regulations?: Array<{
    regulation_id: string;
    title: string;
    requirement?: string;
    status: string;
    measured_value?: string | number;
    required_value?: string;
    visible?: boolean;
    section?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string;
    entities_used?: Array<{
      dbId?: number;
      elementId?: number;
      name?: string;
      type?: string;
    }>;
  }>;
}

export const RisksPage: React.FC = () => {
  const {
    validationResults,
    setValidationResults,
    loadUsers,
    availableUsers,
    setIssueFinancialImpact,
  } = useRulesStore();
  const { selectedModelId, selectedJurisdiction } = useViewerStore();
  const [selectedIssue, setSelectedIssue] = useState<ValidationResult | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [validationReports, setValidationReports] = useState<
    ValidationReport[]
  >([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const hasInitializedRef = useRef(false);

  // Load validation reports for the selected model
  useEffect(() => {
    const loadValidationReports = async () => {
      setIsLoadingReports(true);
      try {
        const reports = await loadComplianceReportsForModel<ValidationReport>(selectedModelId, selectedJurisdiction);

        setValidationReports(reports);
      } catch (error) {
        console.error("Error loading validation reports:", error);
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadValidationReports();
  }, [selectedModelId, selectedJurisdiction]);

  // When validation reports load, seed the global validationResults in the store
  // so that IssueManagementPanel edits (assignments, status, etc.) are reflected in the table.
  // Only initialize once to avoid overwriting user edits (assignments, etc.)
  useEffect(() => {
    if (validationReports.length === 0 || hasInitializedRef.current) return;

    const results: ValidationResult[] = [];

    validationReports.forEach((report) => {
      // Handle v5 format (rule_validations), v4 format (regulation_validation_results), v3 format (validation_results), and old format (regulations)
      const regulations: any[] =
        report.rule_validations ||
        report.regulation_validation_results ||
        report.validation_results ||
        report.regulations ||
        [];
      if (regulations.length === 0) return;

      // Determine if this is a door report or toilet report
      const isDoorReport = report.report_type === 'door' || !!report.door_db_id;

      // Get element ID from door_db_id, wc_db_id, or other formats
      const elementDbId = report.door_db_id || report.wc_db_id
        ? String(report.door_db_id || report.wc_db_id)
        : report.validation_metadata?.room_id
          ? String(report.validation_metadata.room_id)
          : report.metadata?.element_id
            ? String(report.metadata.element_id)
            : String(report.toilet_element_id || "unknown");

      regulations.forEach((regulation: any) => {
        // Filter by visible field - only hide if explicitly set to false
        if (regulation.visible === false) {
          return;
        }

        const regStatus = regulation.status?.toUpperCase();
        const ruleNumber = regulation.rule_number || regulation.regulation_id || "";

        // BNE: only show regulations matching Compliance Hub allowlist
        if (selectedModelId === 'bne') {
          const bneAllowedPrefixes = [
            '604.2.2',
            '604.5.1.1', '604.5.1.2.3', '604.5.2',
            '1103.11.1', '1103.11.2.4.1', '1103.11.2.4.4',
            '404.2.2', '404.2.3.2',
          ];
          const isAllowed = bneAllowedPrefixes.some(p =>
            ruleNumber === p || ruleNumber.startsWith(p + '.') || ruleNumber.startsWith(p + '_'));
          if (!isAllowed) return;
        }

        // For Dutch Bros, hide specific PASS rules
        if (selectedModelId === 'dutch-bros' && regStatus === 'PASS') {
          if (ruleNumber === '604.5' || ruleNumber === '404.2.4.3' || ruleNumber === '404.2.5') return;
        }

        let status: "pass" | "fail" | "warning" | "info" = "warning";
        if (regStatus === "PASS") {
          status = "pass";
        } else if (regStatus === "FAIL") {
          status = "fail";
        } else if (
          regStatus === "WARNING" ||
          regStatus === "NOT_TESTED_MISSING_ELEMENTS" ||
          regStatus === "NOT_TESTED_INSUFFICIENT_DATA" ||
          regStatus === "NOT_APPLICABLE"
        ) {
          status = "warning";
        } else {
          status = "info";
        }

        // Get regulation ID from v5 format (rule_number) or older formats (regulation_id)
        let regulationId = regulation.rule_number || regulation.regulation_id || "unknown";
        // BNE: collapse sub-rules into parent (e.g., 604.5.1.1.1 → 604.5.1.1)
        if (selectedModelId === 'bne') {
          if (regulationId.startsWith('604.5.1.1.')) regulationId = '604.5.1.1';
          else if (regulationId.startsWith('604.5.2.')) regulationId = '604.5.2';
        }

        // Get rule name from v5 format (rule_name) or older formats (title)
        const ruleName = regulation.rule_name || regulation.title || regulationId;

        // Extract element ID from v5 format (involved_db_ids) or older formats (entities_used)
        let elementId = 0;
        if (regulation.involved_db_ids && regulation.involved_db_ids.length > 0) {
          elementId = regulation.involved_db_ids[0];
        } else if (regulation.entities_used && regulation.entities_used.length > 0) {
          const firstEntity = regulation.entities_used[0];
          elementId =
            firstEntity.dbId ||
            firstEntity.elementId ||
            parseInt(elementDbId) ||
            0;
        } else {
          elementId = parseInt(elementDbId) || 0;
        }

        // Get room_name from validation_metadata for grouping by unit
        const roomName =
          report.room_name ||
          report.validation_metadata?.room_name ||
          report.metadata?.element_name ||
          (isDoorReport ? `Door ${elementDbId}` : `Unit ${elementDbId}`);

        // Build entities_used from involved_db_ids if needed
        const entitiesUsed = regulation.entities_used ||
          (regulation.involved_db_ids ? regulation.involved_db_ids.map((dbId: number) => ({ dbId })) : []);

        // Determine element type label for display
        const elementTypeLabel = isDoorReport ? "Door" : "Toilet";

        // Determine rule ID prefix based on report type
        const ruleIdPrefix = isDoorReport ? "ada-404" : "ada-604";

        results.push({
          ruleId: `${ruleIdPrefix}-${regulationId}`,
          ruleName: ruleName,
          standard:
            report.validation_metadata?.standard ||
            report.metadata?.standard ||
            report.standard ||
            "ICC A117.1-2017",
          status: status,
          elementId: elementId,
          elementName: `${elementTypeLabel} ${elementDbId} - ${ruleName}`,
          message: `${regulationId}: ${ruleName}. ${regulation.measured_value ? `Measured: ${regulation.measured_value}` : ""} ${regulation.required_value ? `Required: ${regulation.required_value}` : ""}`,
          data: {
            details: {
              regulationId: regulationId,
              section:
                regulation.section ||
                regulationId?.split(".").slice(0, 2).join("."),
              status: regulation.status,
              measuredValue: regulation.measured_value,
              requiredValue: regulation.required_value,
              jerseySpecific: regulation.jersey_specific,
              notes: regulation.notes || regulation.llm_notes?.join("; "),
              confidence: regulation.confidence,
              severity: regulation.severity,
              entitiesUsed: entitiesUsed,
              toiletElementId: elementDbId,
              roomName: roomName,
              involvedDbIds: regulation.involved_db_ids || [],
              reportType: isDoorReport ? 'door' : 'toilet',
            },
          },
          timestamp: new Date(),
        });
      });
    });

    // For Dutch Bros Florida, filter out 404.2.3 (use 404.2.4 instead)
    const filteredResults = (selectedModelId === 'dutch-bros' && selectedJurisdiction?.toLowerCase().includes('florida'))
      ? results.filter((r) => r.data?.details?.regulationId !== '404.2.3')
      : results;

    // Preserve any existing issue management state in the store by merging
    // Store data (assignments, priorities, etc.) takes precedence over base data
    const currentResults = useRulesStore.getState().validationResults;
    const mergedResults = filteredResults.map((base) => {
      const existing = currentResults.find(
        (r) => r.ruleId === base.ruleId && r.elementId === base.elementId
      );
      // If existing has issue management data, preserve it and merge with base
      if (existing) {
        return {
          ...base,
          // Preserve all issue management fields from existing
          issueId: existing.issueId,
          issueStatus: existing.issueStatus,
          issuePriority: existing.issuePriority,
          assignment: existing.assignment,
          dueDate: existing.dueDate,
          notes: existing.notes,
          resolutionNote: existing.resolutionNote,
          resolvedAt: existing.resolvedAt,
          resolvedBy: existing.resolvedBy,
          financialImpact: existing.financialImpact,
          // Keep base data for fields that might have been updated in reports
          ruleName: base.ruleName,
          elementName: base.elementName,
          message: base.message,
          status: base.status,
          standard: base.standard,
          timestamp: base.timestamp,
          data: {
            ...base.data,
            // Merge details, but preserve any custom fields from existing
            details: {
              ...base.data?.details,
              ...existing.data?.details,
            },
          },
        };
      }
      return base;
    });
    setValidationResults(mergedResults);
    hasInitializedRef.current = true;
  }, [validationReports, setValidationResults]);

  // Load users when component mounts
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Filter and sort results based on search and filters
  const filteredResults = useMemo(() => {
    const filtered = validationResults.filter((result) => {
      const matchesSearch =
        result.ruleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.elementName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.message.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || result.status === statusFilter;

      const matchesPriority =
        priorityFilter === "all" || result.issuePriority === priorityFilter;

      const matchesAssignee =
        assigneeFilter === "all" ||
        result.assignment?.assignedTo?.id === assigneeFilter;

      return (
        matchesSearch && matchesStatus && matchesPriority && matchesAssignee
      );
    });

    // Sort by status: fail first, then warning, then pass, then info
    const statusOrder: Record<string, number> = {
      fail: 0,
      warning: 1,
      pass: 2,
      info: 3,
    };

    return filtered.sort((a, b) => {
      const aOrder = statusOrder[a.status] ?? 99;
      const bOrder = statusOrder[b.status] ?? 99;
      return aOrder - bOrder;
    });
  }, [
    validationResults,
    searchTerm,
    statusFilter,
    priorityFilter,
    assigneeFilter,
  ]);

  // Calculate progress stats
  const progressStats = useMemo(() => {
    const completed = validationResults.filter(
      (r) => r.issueStatus === "resolved" || r.issueStatus === "closed" || r.status === "pass"
    ).length;
    const total = validationResults.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }, [validationResults]);

  // Stable key based on issueId or ruleId+elementId — does not depend on array index
  const getResultKey = (result: ValidationResult) =>
    result.issueId || `${result.ruleId}-${result.elementId}`;

  const toggleSelectItem = (key: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Clear stale selections when filters change
  const filteredKeys = useMemo(
    () => new Set(filteredResults.map((r) => getResultKey(r))),
    [filteredResults]
  );
  const activeSelectedItems = useMemo(
    () => new Set([...selectedItems].filter((key) => filteredKeys.has(key))),
    [selectedItems, filteredKeys]
  );

  const toggleSelectAll = () => {
    if (activeSelectedItems.size === filteredResults.length && filteredResults.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredResults.map((r) => getResultKey(r))));
    }
  };

  const handleBatchAssign = (userId: string) => {
    const user = availableUsers.find((u) => u.id === userId) || null;
    const selectedKeys = activeSelectedItems;
    const currentUser = useRulesStore.getState().currentUser;

    const updatedResults = validationResults.map((result) => {
      const key = getResultKey(result);
      if (!selectedKeys.has(key)) return result;
      return {
        ...result,
        issueId: result.issueId || `issue-${result.elementId}-${result.ruleId}`,
        assignment: {
          assignedTo: user || undefined,
          assignedBy: currentUser || undefined,
          assignedAt: new Date(),
        },
        issueStatus: user ? ("in_progress" as const) : ("open" as const),
      };
    });
    setValidationResults(updatedResults);
    setSelectedItems(new Set());
    const userName = user?.name || "Unassigned";
    toast.success(`${selectedKeys.size} item${selectedKeys.size !== 1 ? "s" : ""} assigned to ${userName}`);
  };

  return (
    <div className="min-h-screen bg-custom-light-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Loading State */}
        {isLoadingReports && (
          <div className="bg-white rounded-lg shadow-sm border border-custom-light-200 p-6 mb-6">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-custom-navy mb-4"></div>
              <p className="text-custom-grey">Loading validation reports...</p>
            </div>
          </div>
        )}

        {/* Filters */}
        {!isLoadingReports && (
          <div className="bg-white rounded-lg shadow-sm border border-custom-light-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-custom-navy mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search rules, elements, messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 bg-custom-light-50 border border-custom-light-200 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-navy focus:border-custom-navy text-custom-navy placeholder-custom-grey"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-custom-navy mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-custom-light-50 border border-custom-light-200 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-navy focus:border-custom-navy text-custom-navy"
                >
                  <option value="all">All Status</option>
                  <option value="fail">Fail</option>
                  <option value="warning">Warning</option>
                  <option value="pass">Pass</option>
                  <option value="info">Info</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-custom-navy mb-2">
                  Priority
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-custom-light-50 border border-custom-light-200 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-navy focus:border-custom-navy text-custom-navy"
                >
                  <option value="all">All Priority</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Assignee Filter */}
              <div>
                <label className="block text-sm font-medium text-custom-navy mb-2">
                  Assignee
                </label>
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-custom-light-50 border border-custom-light-200 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-navy focus:border-custom-navy text-custom-navy"
                >
                  <option value="all">All Assignees</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role || "team member"})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Overall Progress */}
        {!isLoadingReports && (
          <div className="bg-white rounded-lg shadow-sm border border-custom-light-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-custom-navy">
                Overall Progress
              </h3>
              <span className="text-sm text-custom-grey">
                {progressStats.completed} of {progressStats.total} completed
              </span>
            </div>
            <div className="w-full bg-custom-light-200 rounded-full h-6 relative p-1">
              <div
                className="bg-green-500 h-4 rounded-full transition-all duration-300 relative"
                style={{
                  width: `${progressStats.percentage}%`,
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-white drop-shadow-lg">
                    {progressStats.percentage}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-between text-xs text-custom-grey mt-2">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
        )}

        {/* Results Table */}
        {!isLoadingReports && (
          <div className="bg-white rounded-lg shadow-sm border border-custom-light-200">
            {/* Batch Assign Bar */}
            {activeSelectedItems.size > 0 && (
              <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">
                  {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""} selected
                </span>
                <div className="flex items-center gap-2">
                  <select
                    className="px-3 py-1.5 text-sm bg-white border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-custom-navy"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBatchAssign(e.target.value);
                        e.target.value = "";
                      }
                    }}
                  >
                    <option value="" disabled>Assign to...</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setSelectedItems(new Set())}
                    className="px-3 py-1.5 text-sm text-custom-grey hover:text-custom-navy bg-white border border-custom-light-200 rounded-md hover:bg-custom-light-50 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Table Summary */}
            <div className="px-6 py-3 bg-custom-light-50 border-b border-custom-light-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-custom-navy">
                  {filteredResults.length} out of {validationResults.length} issues of 604 ADA
                </span>
                {filteredResults.length > 10 && (
                  <span className="text-xs text-custom-grey">
                    Scroll to see more
                  </span>
                )}
              </div>
            </div>
            <div className="overflow-y-auto max-h-[600px] scrollbar-thin scrollbar-thumb-custom-light-200 scrollbar-track-custom-light-50">
              <table className="min-w-full divide-y divide-custom-light-200">
                <thead className="bg-custom-light-50 sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-3">
                      <input
                        type="checkbox"
                        checked={activeSelectedItems.size === filteredResults.length && filteredResults.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-custom-navy focus:ring-custom-navy cursor-pointer"
                      />
                    </th>
                    <th className="w-20 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Status
                    </th>
                    <th className="w-36 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Message
                    </th>
                    <th className="w-24 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Element
                    </th>
                    <th className="w-40 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Assigned & Priority
                    </th>
                    <th className="w-28 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="w-24 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Status
                    </th>
                    <th className="w-28 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Financial Impact
                    </th>
                    <th className="w-24 px-3 py-3 text-left text-xs font-medium text-custom-grey uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-custom-light-200">
                  {filteredResults.map((result, index) => (
                    <tr
                      key={`${result.ruleId}-${result.elementId}-${index}`}
                      className={`hover:bg-custom-light-50 transition-colors duration-150 cursor-pointer ${
                        activeSelectedItems.has(getResultKey(result)) ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-3 py-4 w-10">
                        <input
                          type="checkbox"
                          checked={activeSelectedItems.has(getResultKey(result))}
                          onChange={() => toggleSelectItem(getResultKey(result))}
                          className="w-4 h-4 rounded border-gray-300 text-custom-navy focus:ring-custom-navy cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-4 w-20 whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            result.status === "fail"
                              ? "bg-red-100 text-red-700"
                              : result.status === "warning"
                                ? "bg-yellow-100 text-yellow-700"
                                : result.status === "pass"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-custom-light-100 text-custom-grey"
                          }`}
                          title={`Status: ${result.status === "pass" ? "CLOSED" : result.status.toUpperCase()}`}
                        >
                          {result.status === "pass" ? "CLOSED" : result.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-4 w-36">
                        <div
                          className="text-sm text-custom-navy break-words"
                          title={result.message}
                        >
                          {result.message}
                        </div>
                      </td>
                      <td className="px-3 py-4 w-24">
                        <div
                          className="text-sm text-custom-navy break-words"
                          title={`${result.elementName} (ID: ${result.elementId})`}
                        >
                          {result.elementName}
                        </div>
                        <div
                          className="text-xs text-custom-grey break-words"
                          title={`Element ID: ${result.elementId}`}
                        >
                          ID: {result.elementId}
                        </div>
                      </td>
                      <td className="px-3 py-4 w-40 whitespace-nowrap">
                        <div className="flex flex-col gap-2">
                          {/* Assigned To */}
                          <div
                            title={
                              result.assignment?.assignedTo
                                ? `Assigned to: ${result.assignment.assignedTo.name}`
                                : "Not assigned"
                            }
                          >
                            {result.assignment?.assignedTo ? (
                              <div className="flex items-center gap-1">
                                <div className="w-5 h-5 rounded-full bg-custom-navy text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                                  {result.assignment.assignedTo.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)}
                                </div>
                                <span
                                  className="text-sm text-custom-grey truncate"
                                  title={result.assignment.assignedTo.name}
                                >
                                  {result.assignment.assignedTo.name}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-custom-grey">
                                Unassigned
                              </span>
                            )}
                          </div>
                          {/* Priority */}
                          <div
                            title={
                              result.issuePriority
                                ? `Priority: ${result.issuePriority.toUpperCase()}`
                                : "No priority set"
                            }
                          >
                            {result.issuePriority ? (
                              <span
                                className={`inline-block px-2 py-1 rounded text-xs font-medium text-white ${
                                  result.issuePriority === "critical"
                                    ? "bg-red-600"
                                    : result.issuePriority === "high"
                                      ? "bg-orange-600"
                                      : result.issuePriority === "medium"
                                        ? "bg-yellow-600"
                                        : "bg-green-600"
                                }`}
                              >
                                {result.issuePriority.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-sm text-custom-grey">-</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 w-28 whitespace-nowrap">
                        <div
                          className="text-sm text-custom-navy"
                          title={`Due Date: ${
                            result.dueDate
                              ? new Date(result.dueDate).toLocaleString()
                              : "No due date set"
                          }`}
                        >
                          {result.dueDate
                            ? new Date(result.dueDate).toLocaleDateString()
                            : "-"}
                        </div>
                      </td>
                      <td className="px-3 py-4 w-24 whitespace-nowrap">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            result.status === "pass"
                              ? "bg-green-100 text-green-700"
                              : result.issueStatus === "open"
                                ? "bg-blue-100 text-blue-700"
                                : result.issueStatus === "in_progress"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : result.issueStatus === "resolved"
                                    ? "bg-green-100 text-green-700"
                                    : result.issueStatus === "closed"
                                      ? "bg-green-100 text-green-700"
                                      : result.issueStatus === "pending"
                                        ? "bg-custom-light-100 text-custom-grey"
                                        : "bg-custom-light-100 text-custom-grey"
                          }`}
                          title={`Issue Status: ${result.status === "pass" ? "Closed" : result.issueStatus || "Not set"}`}
                        >
                          {result.status === "pass"
                            ? "Closed"
                            : result.issueStatus === "in_progress"
                              ? "In Progress"
                              : result.issueStatus === "open"
                                ? "Open"
                                : result.issueStatus === "resolved"
                                  ? "Resolved"
                                  : result.issueStatus === "closed"
                                    ? "Closed"
                                    : result.issueStatus === "pending"
                                      ? "Pending"
                                      : "Not Set"}
                        </span>
                      </td>
                      <td className="px-3 py-4 w-28 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-custom-grey">$</span>
                          <input
                            type="number"
                            className="w-full px-2 py-1 text-sm bg-custom-light-50 border border-custom-light-200 rounded focus:outline-none focus:ring-2 focus:ring-custom-navy focus:border-custom-navy text-custom-navy"
                            placeholder="0"
                            value={
                              Number.isFinite(result.financialImpact as number)
                                ? String(result.financialImpact)
                                : ""
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              const issueId =
                                result.issueId ||
                                `issue-${result.elementId}-${result.ruleId}`;
                              if (raw === "") {
                                void setIssueFinancialImpact(issueId, null);
                                return;
                              }
                              const parsed = Number(raw);
                              if (Number.isNaN(parsed)) {
                                return;
                              }
                              void setIssueFinancialImpact(issueId, parsed);
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-4 w-24 whitespace-nowrap text-left text-sm font-medium">
                        <button
                          onClick={() => setSelectedIssue(result)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          title="Manage this risk - assign, set priority, or update status"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingReports && filteredResults.length === 0 && (
          <div className="text-center py-12">
            <div className="text-custom-grey">
              {validationResults.length === 0
                ? "No validation results found. Run a validation to see issues here."
                : "No issues match your current filters."}
            </div>
          </div>
        )}
      </div>

      {/* Issue Management Modal */}
      {selectedIssue && (
        <IssueManagementPanel
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
        />
      )}
    </div>
  );
};
